import React from 'react'
import { circle, offset, plume, plumeTiers, plumeTrack, rings, straightTrack, streakLines, tierPolygon } from './model'

// Check Wind's marks on the map, drawn in lat/lng and projected each move so they stay true to the ground
// on the tilted 3D map too: distance rings round the cone, the wind cone's three tiers, and the cone's point.
// With Thermals on, `path` is the Thermals API's plume and the same tiers bend along it, cut from its length.

// Back to front: where deer smell it faintly, the fringe, where it is strong.
const TIERS = [
  { fill: '#E4E6E1', opacity: 0.45, blur: 16 },
  { fill: '#F18880', opacity: 0.6, blur: 12 },
  { fill: '#ff2a1f', opacity: 0.75, blur: 3 }
]

const pathOf = (points, project, close) => {
  const px = points.map(project).filter(Boolean)
  if (px.length < 2) return ''
  return `M${px.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join('L')}${close ? 'Z' : ''}`
}

export default function MapMarks({ surface, origin, wind, hour, path, metric }) {
  const { width, height } = surface.size()
  const at = surface.project(origin)
  if (!at || !width) return null
  const metresPerPx = surface.metresPerPx(origin.lat)
  // While 3D terrain is still loading under the camera, points can project wildly off; skip those frames.
  const east = surface.project(offset(origin, 100, 0))
  const eastPx = east ? Math.hypot(east.x - at.x, east.y - at.y) : 0
  if (!(eastPx > 100 / metresPerPx / 3 && eastPx < 100 / metresPerPx * 3)) return null
  const reach = Math.hypot(Math.max(at.x, width - at.x), Math.max(at.y, height - at.y))
  const project = p => surface.project(p)
  const bearing = wind && wind.direction != null ? (wind.direction + 180) % 360 : null
  // Ring labels sit on the side the scent is not drifting toward.
  const side = bearing != null && Math.sin(bearing * Math.PI / 180) > 0 ? -1 : 1

  const ringList = rings(metresPerPx, reach, metric)

  let cone = null
  if (bearing != null) {
    const design = plume(wind.speed, wind.gust, hour, wind.cloudCover)
    const a = design.a
    const track = path ? plumeTrack(path.points) : straightTrack(origin, bearing)
    // A plume that loops back on itself reaches only as far as the track runs; never shorter than the
    // design's shortest cone.
    const R = path ? plumeTiers(Math.max(60, track.looped ? track.length : path.lengthM || track.length)) : design.R
    // The fade runs straight from the point to just past the tip; on a bent plume, to its far end.
    const tip = project(track.at(R[0] * 1.05, 0))
    const dash = Math.max(8, Math.min(44, 6 + (wind.speed || 0) * 1.7))
    const pace = Math.max(2, Math.min(5.5, 5.5 - (wind.speed || 0) * 0.2))
    cone = {
      tip,
      tiers: TIERS.map((t, i) => ({ ...t, d: pathOf(tierPolygon(track, R[i], a), project, true) })),
      streaks: streakLines(track, R[0], a).map((s, k) => ({
        d: pathOf(s.points, project, false),
        opacity: s.opacity,
        dur: pace * (0.85 + 0.2 * ((k * 7) % 10) / 10),
        begin: -((k * 0.137) % 1) * pace
      })),
      dash
    }
  }

  return (
    <svg className='check-wind-marks' width={width} height={height}>
      <defs>
        {TIERS.map((t, i) => (
          <filter key={i} id={`check-wind-soft-${i}`} x='-50%' y='-50%' width='200%' height='200%'>
            <feGaussianBlur stdDeviation={t.blur} />
          </filter>
        ))}
        {cone && cone.tip ? (
          <>
            <linearGradient id='check-wind-fade' gradientUnits='userSpaceOnUse' x1={at.x} y1={at.y} x2={cone.tip.x} y2={cone.tip.y}>
              <stop offset='0' stopColor='#fff' stopOpacity='1' />
              <stop offset='.55' stopColor='#fff' stopOpacity='.9' />
              <stop offset='.85' stopColor='#fff' stopOpacity='.45' />
              <stop offset='1' stopColor='#fff' stopOpacity='0' />
            </linearGradient>
            <mask id='check-wind-mask' maskUnits='userSpaceOnUse' x={-width} y={-height} width={width * 3} height={height * 3}>
              <rect x={-width} y={-height} width={width * 3} height={height * 3} fill='url(#check-wind-fade)' />
            </mask>
          </>
        ) : null}
      </defs>

      {ringList.map(r => {
        const d = pathOf(circle(origin, r.metres), project, true)
        return (
          <g key={r.label}>
            <path d={d} fill='none' stroke='#000' strokeWidth='3' opacity='.18' />
            <path d={d} fill='none' stroke='#E4E6E1' strokeWidth='1.2' opacity='.45' />
          </g>
        )
      })}

      {cone ? (
        <>
          <g mask='url(#check-wind-mask)'>
            {cone.tiers.map((t, i) => (
              <path key={i} d={t.d} fill={t.fill} opacity={t.opacity} filter={`url(#check-wind-soft-${i})`} />
            ))}
          </g>
          <g mask='url(#check-wind-mask)' stroke='#FFE1DC' strokeWidth='1.5' strokeLinecap='round' fill='none'>
            {cone.streaks.map((s, k) => (
              <path key={k} d={s.d} opacity={s.opacity.toFixed(2)} strokeDasharray={`${cone.dash.toFixed(1)} 120`}>
                <animate attributeName='stroke-dashoffset' from={(cone.dash + 120).toFixed(1)} to='0'
                  dur={`${s.dur.toFixed(2)}s`} begin={`${s.begin.toFixed(2)}s`} repeatCount='indefinite' />
              </path>
            ))}
          </g>
        </>
      ) : null}

      <circle cx={at.x} cy={at.y} r='3' fill='#ff2a1f' />

      {ringList.map(r => {
        const a = project(offset(origin, side * r.metres, 0)), b = project(offset(origin, -side * r.metres, 0))
        return [a, b].filter(Boolean).map((p, i) => (
          <text key={`${r.label}-${i}`} x={p.x} y={p.y} className='check-wind-ring-label'>{r.label}</text>
        ))
      })}
    </svg>
  )
}
